import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    background: 'var(--bg-primary)',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '40px 32px',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--shadow)',
  },
  logo: {
    fontSize: '32px',
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: '8px',
    background: 'linear-gradient(135deg, var(--accent), #f7a)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  subtitle: {
    textAlign: 'center',
    color: 'var(--text-secondary)',
    fontSize: '14px',
    marginBottom: '32px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  footer: {
    textAlign: 'center',
    marginTop: '24px',
    fontSize: '14px',
    color: 'var(--text-secondary)',
  },
  link: {
    color: 'var(--accent)',
    textDecoration: 'none',
    fontWeight: '500',
  },
  devSection: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  devLabel: {
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    color: 'var(--text-muted)',
    textAlign: 'center',
    margin: '4px 0',
  },
  devBtn: {
    padding: '10px 14px',
    fontSize: '13px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-elevated)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center',
    transition: 'background 150ms',
  },
  devBtnAccent: {
    padding: '10px 14px',
    fontSize: '13px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--accent)',
    background: 'rgba(232, 87, 109, 0.08)',
    color: 'var(--accent)',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'center',
    fontWeight: '500',
    transition: 'background 150ms',
  },
  devStatus: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    textAlign: 'center',
    padding: '4px 0',
  },
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [devLoading, setDevLoading] = useState('');
  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/discover');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminLoading(true);

    try {
      await login('admin@nova.com', adminPassword);
      navigate('/admin');
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Invalid admin password');
    } finally {
      setAdminLoading(false);
    }
  };

  const handleQuickAdminLogin = async () => {
    setAdminError('');
    setDevLoading('admin');

    try {
      await login('admin@nova.com', 'testtest');
      navigate('/admin');
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Admin login failed');
    } finally {
      setDevLoading('');
    }
  };

  const handleRandomLogin = async (sex) => {
    setAdminError('');
    setDevLoading(sex);

    try {
      const res = await api.post('/auth/dev/random-user', { sex });
      loginWithToken(res.data.token, res.data.user);
      navigate('/discover');
    } catch (err) {
      setAdminError(err.response?.data?.error || 'Failed to create random user');
    } finally {
      setDevLoading('');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.logo}>nova</h1>
        <p style={styles.subtitle}>Welcome back</p>

        <form style={styles.form} onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                style={{ paddingRight: '48px' }}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                padding: 8, fontSize: 16, minWidth: 44, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {showPassword ? '\u{1F648}' : '\u{1F441}'}
              </button>
            </div>
          </div>

          {error && <p className="error-text">{error}</p>}

          <button
            type="submit"
            className="btn btn-primary btn-full"
            disabled={loading}
            style={{ marginTop: '8px', padding: '12px' }}
          >
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p style={styles.footer}>
          Don&apos;t have an account?{' '}
          <Link to="/register" style={styles.link}>
            Sign up
          </Link>
        </p>

        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <button
            onClick={() => setShowAdmin(!showAdmin)}
            style={{ fontSize: '12px', color: 'var(--text-muted)', cursor: 'pointer', background: 'none', border: 'none' }}
          >
            {showAdmin ? '▾ Dev Tools' : '▸ Dev Tools'}
          </button>
          {showAdmin && (
            <div style={styles.devSection}>
              <div style={styles.devLabel}>Quick Login</div>

              <button
                style={styles.devBtnAccent}
                onClick={handleQuickAdminLogin}
                disabled={!!devLoading}
              >
                {devLoading === 'admin' ? 'Logging in...' : '🔑 Login as Admin Tom'}
              </button>

              <div style={styles.devLabel}>Generate Random User</div>

              <button
                style={styles.devBtn}
                onClick={() => handleRandomLogin('male')}
                disabled={!!devLoading}
              >
                {devLoading === 'male' ? '⏳ Creating profile...' : '👤 Log in as random male'}
              </button>

              <button
                style={styles.devBtn}
                onClick={() => handleRandomLogin('female')}
                disabled={!!devLoading}
              >
                {devLoading === 'female' ? '⏳ Creating profile...' : '👤 Log in as random female'}
              </button>

              {devLoading && (
                <div style={styles.devStatus}>
                  Generating profile, fetching photo...
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '10px', marginTop: '4px' }}>
                <div style={styles.devLabel}>Manual Admin Login</div>
                <form onSubmit={handleAdminLogin} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Admin password"
                    required
                    style={{ fontSize: '13px' }}
                  />
                  <button
                    type="submit"
                    className="btn btn-secondary btn-full"
                    disabled={adminLoading}
                    style={{ padding: '10px', fontSize: '13px' }}
                  >
                    {adminLoading ? 'Signing in...' : 'Sign in as Admin'}
                  </button>
                </form>
              </div>

              {adminError && <p className="error-text" style={{ marginTop: '4px' }}>{adminError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
